import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Avatar,
} from '@mui/material';
import { Add, Refresh, Delete, ArrowBack, Visibility, YouTube, PlayCircle } from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';

const getYouTubeId = (url) => {
  if (!url) return null;
  if (url.length === 11 && !url.includes('youtube.com') && !url.includes('youtu.be')) return url;
  const regExp = /^.*((youtu\.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7]?.length === 11) ? match[7] : null;
};

export default function Stories() {
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('list'); 
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewStory, setViewStory] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', videoMode: 'youtube',
    youtube_url: '', duration: '5',
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const load = useCallback(async (cls) => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getStories, {
      standard: cls.standard,
      section: cls.section,
      academic_year: cls.acYear,
    });
    setRecords(data?.stories || []);
    setLoading(false);
  }, []);

  const handleSelect = (cls) => {
    setSelected(cls);
    setView('list');
    setTab('list');
    load(cls);
  };

  const handleSave = async () => {
    if (!form.title) { alert('Title is required'); return; }
    if (form.videoMode === 'youtube' && !form.youtube_url.trim()) { alert('YouTube URL is required'); return; }
    if (form.videoMode === 'upload' && !videoFile) { alert('Please select a video file'); return; }

    setSaving(true);
    try {
      let videoFileName = '';
      let thumbnailFileName = '';

      if (form.videoMode === 'youtube') {
        videoFileName = form.youtube_url.trim();
      } else {
        const fd = new FormData();
        fd.append('story-video', videoFile);
        const res = await postImg(Config.main_url + apiList.addStoryVideo, fd);
        if (res?.code === '200') videoFileName = res.videoName || '';
        else { alert('Video upload failed'); setSaving(false); return; }
      }

      if (thumbnail) {
        const fd = new FormData();
        fd.append('story-thumbnail', thumbnail);
        const res = await postImg(Config.main_url + apiList.addStoryThumbnail, fd);
        if (res?.code === '200') thumbnailFileName = res.imageName || '';
      }

      const data = await InvokeApi(Config.main_url + apiList.addStory, {
        title: form.title,
        description: form.description,
        video_url: videoFileName,
        thumbnail: thumbnailFileName,
        standard: selected.standard,
        section: selected.section,
        type: form.videoMode === 'youtube' ? 'youtube' : 'video',
        duration: parseInt(form.duration) || 5,
        created_by: '1',
        questions: [{
          question: 'What did you learn from this story?',
          options: ['Very interesting', 'Good content', 'Helpful', 'Excellent'],
          correct_answer: 'Very interesting',
        }],
      });

      if (data?.code === '200' || data?.code === 200) {
        setForm({ title: '', description: '', videoMode: 'youtube', youtube_url: '', duration: '5' });
        setThumbnail(null); setVideoFile(null);
        setTab('list');
        load(selected);
      } else alert(data?.message || 'Failed to add story');
    } catch (e) { alert('Something went wrong'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this story?')) return;
    await InvokeApi(Config.main_url + apiList.deleteStory, { id: id });
    load(selected);
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Learning Stories</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to manage stories</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Learning Stories</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} - {selected?.section} · {selected?.acYear} · {records.length} stories
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Button variant={tab === 'list' ? 'contained' : 'outlined'} onClick={() => setTab('list')}
            sx={tab === 'list'
              ? { background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }
              : { borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>
            Stories List
          </Button>
          <Button variant={tab === 'add' ? 'contained' : 'outlined'} startIcon={<Add />} onClick={() => setTab('add')}
            sx={tab === 'add'
              ? { background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }
              : { borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>
            Add Story
          </Button>
          {tab === 'list' && (
            <Tooltip title="Refresh">
              <IconButton onClick={() => load(selected)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {tab === 'list' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#', 'Thumbnail', 'Title', 'Type', 'Duration', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No stories for {selected?.standard} - {selected?.section}
                      </TableCell>
                    </TableRow>
                  ) : records.map((r, i) => {
                    const ytId = r.type === 'youtube' ? getYouTubeId(r.video_url) : null;
                    const thumb = r.thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null);
                    return (
                      <TableRow key={r.ID || i} hover>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                        <TableCell>
                          {thumb ? (
                            <Box component="img" src={thumb} alt={r.title}
                              sx={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 1 }} />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 80, height: 50, background: '#e8eaf6', borderRadius: 1 }}>
                              <PlayCircle sx={{ color: '#1a237e' }} />
                            </Avatar>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600} fontSize={13}>{r.title}</Typography>
                          <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{r.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={r.type === 'youtube' ? 'YouTube' : 'Video'}
                            size="small"
                            icon={r.type === 'youtube' ? <YouTube sx={{ fontSize: '14px !important' }} /> : undefined}
                            sx={{
                              background: r.type === 'youtube' ? '#ffebee' : '#e8eaf6',
                              color: r.type === 'youtube' ? '#c62828' : '#1a237e',
                              fontWeight: 700, fontSize: 11,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.duration || 5} min</TableCell>
                        <TableCell>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => setViewStory(r)} sx={{ color: '#1a237e' }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(r.ID)} sx={{ color: '#c62828' }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {tab === 'add' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3, maxWidth: 600 }}>
          <Typography fontWeight={700} color="#1a237e" mb={2}>
            Add Story — {selected?.standard} - {selected?.section}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Title *" value={form.title} onChange={f('title')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description" value={form.description} onChange={f('description')} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Video Type" value={form.videoMode} onChange={f('videoMode')}>
                <MenuItem value="youtube">YouTube URL</MenuItem>
                <MenuItem value="upload">Upload Video</MenuItem>
              </TextField>
            </Grid>
            {form.videoMode === 'youtube' && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="YouTube URL or Video ID *"
                  value={form.youtube_url} onChange={f('youtube_url')}
                  placeholder="https://youtube.com/watch?v=... or 11-char video ID"
                  helperText={form.youtube_url
                    ? getYouTubeId(form.youtube_url)
                      ? `✓ Valid ID: ${getYouTubeId(form.youtube_url)}`
                      : '⚠ Could not extract YouTube ID'
                    : ''}
                />
                {form.youtube_url && getYouTubeId(form.youtube_url) && (
                  <Box sx={{ mt: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #e8eaf6' }}>
                    <Box component="img"
                      src={`https://img.youtube.com/vi/${getYouTubeId(form.youtube_url)}/hqdefault.jpg`}
                      alt="preview" sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                    <Box sx={{ p: 1, background: '#f5f6fa', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <YouTube sx={{ color: '#FF0000', fontSize: 16 }} />
                      <Typography fontSize={11} color="text.secondary">YouTube preview</Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            )}
            {form.videoMode === 'upload' && (
              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }}>
                  {videoFile ? `✓ ${videoFile.name}` : 'Select Video File'}
                  <input type="file" hidden accept="video/*" onChange={e => setVideoFile(e.target.files[0])} />
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }}>
                {thumbnail ? `✓ ${thumbnail.name}` : 'Upload Thumbnail (optional)'}
                <input type="file" hidden accept="image/*" onChange={e => setThumbnail(e.target.files[0])} />
              </Button>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Duration (minutes)" value={form.duration}
                onChange={f('duration')} type="number" inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, py: 1.2 }}>
                {saving ? <CircularProgress size={22} color="inherit" /> : 'Add Story'}
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      <Dialog open={!!viewStory} onClose={() => setViewStory(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {viewStory?.title}
          <Chip label={viewStory?.type === 'youtube' ? 'YouTube' : 'Video'} size="small"
            sx={{ background: viewStory?.type === 'youtube' ? '#ffebee' : '#e8eaf6', color: viewStory?.type === 'youtube' ? '#c62828' : '#1a237e', fontWeight: 700 }} />
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {viewStory?.type === 'youtube' && getYouTubeId(viewStory?.video_url) ? (
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <iframe
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                src={`https://www.youtube.com/embed/${getYouTubeId(viewStory.video_url)}?autoplay=1`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title={viewStory?.title}
              />
            </Box>
          ) : viewStory?.video_url ? (
            <video controls style={{ width: '100%', maxHeight: 400 }} src={viewStory.video_url} />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No video available</Typography>
            </Box>
          )}
          {viewStory?.description && (
            <Box sx={{ p: 2 }}>
              <Typography fontSize={14} color="text.secondary">{viewStory.description}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setViewStory(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
